import { Layout, Structure, StructureGroupConfig, StructureSlot } from 'data/generated/graphql';

// Local augmentation: Layout in the generated types does not yet include structure because
// `yarn graphql` has not been run since the BE schema was updated. Remove this once types are regenerated.
export type EditorLayout = Layout & { structure?: Structure };

export const DND_TYPE_GROUP = 'GROUP';
export const DND_TYPE_MODULE = 'MODULE';
export const DND_GROUP_ID_PREFIX = 'group-';

// Slots stamped with their groupId for the flat-list model used during layout editing.
export type FlatSlot = StructureSlot & { groupId: string };

/** Derives a flat slot list from layout.structure.groups, stamping each slot with its groupId. */
export const deriveModules = (layout: EditorLayout): FlatSlot[] =>
  (layout.structure?.groups ?? []).flatMap((group) => group.modules.map((m) => ({ ...m, groupId: group.id })));

/** Partitions a flat slot list into a Map keyed by groupId ('' for ungrouped). */
export const groupModulesByGroupId = (modules: FlatSlot[]): Map<string, FlatSlot[]> => {
  const byGroup = new Map<string, FlatSlot[]>();
  modules.forEach((m) => {
    const gid = m.groupId;
    if (!byGroup.has(gid)) byGroup.set(gid, []);
    byGroup.get(gid)!.push(m);
  });
  return byGroup;
};

/** Returns a new array with the item at `index` swapped with its neighbour in `direction`. */
export const swapAdjacentItems = <T>(arr: T[], index: number, direction: 'up' | 'down'): T[] => {
  const next = [...arr];
  const swapIndex = direction === 'up' ? index - 1 : index + 1;
  [next[swapIndex], next[index]] = [next[index], next[swapIndex]];
  return next;
};

/** Writes a flat slot list back into layout.structure.groups, partitioning by groupId. */
export const applyModulesToLayout = (layout: EditorLayout, modules: FlatSlot[]): EditorLayout => {
  const byGroup = groupModulesByGroupId(modules);
  return {
    ...layout,
    structure: {
      groups: (layout.structure?.groups ?? []).map((g) => ({
        ...g,
        modules: (byGroup.get(g.id) ?? []).map(({ groupId: _groupId, ...slot }) => slot)
      }))
    }
  };
};

export const updateModuleInList = (modules: FlatSlot[], index: number, updated: FlatSlot): FlatSlot[] => {
  const next = [...modules];
  next[index] = updated;
  return next;
};

export const addModuleToGroup = (
  modules: FlatSlot[],
  module: FlatSlot,
  groupId: string | null,
  localInsertIndex?: number
): FlatSlot[] => {
  const next = [...modules];
  const groupIndices = next.reduce<number[]>((acc, m, i) => {
    if (m.groupId === groupId) acc.push(i);
    return acc;
  }, []);

  let insertAt: number;
  if (groupIndices.length === 0) {
    insertAt = next.length;
  } else if (localInsertIndex !== undefined && localInsertIndex < groupIndices.length) {
    insertAt = groupIndices[localInsertIndex];
  } else {
    insertAt = groupIndices[groupIndices.length - 1] + 1;
  }

  next.splice(insertAt, 0, module);
  return next;
};

export const removeModuleFromList = (modules: FlatSlot[], index: number): FlatSlot[] => {
  const next = [...modules];
  next.splice(index, 1);
  return next;
};

/**
 * Extracts the layout ID from an allessehId.
 * Format: takes everything after the second underscore and converts to lowercase.
 * Example: "WSJ_PAGE_homepage" -> "homepage"
 */
export const getLayoutId = (allessehId: string): string => allessehId.split('_').slice(2).join('_').toLowerCase();

/**
 * Gets the layout ID for a page, with support for URL parameters and baseline fallback.
 * Used when creating/editing pages to determine which layout to use.
 */
export const getLayoutIdForPage = ({
  allessehId,
  dtoLayoutId,
  isCreatingNewPage,
  layoutIdParam
}: {
  allessehId: string;
  dtoLayoutId?: string | null;
  isCreatingNewPage: boolean;
  layoutIdParam?: string | null;
}): string => {
  let layoutId = getLayoutId(allessehId);

  if (isCreatingNewPage) {
    // If creating new page and there's a layoutId param, use it; otherwise use baseline
    layoutId = layoutIdParam ?? 'baseline';
  } else if (typeof dtoLayoutId === 'string' && dtoLayoutId.includes('baseline')) {
    layoutId = 'baseline';
  }

  return layoutId;
};

export interface GroupViolation {
  message: string;
}

export function validateGroup(config: StructureGroupConfig, modules: FlatSlot[]): GroupViolation[] {
  const violations: GroupViolation[] = [];
  const count = modules.length;

  if (config.min != null && count < config.min) {
    violations.push({ message: `Needs at least ${config.min} module${config.min !== 1 ? 's' : ''} (has ${count})` });
  }
  if (config.max != null && count > config.max) {
    violations.push({
      message: `Exceeds maximum of ${config.max} module${config.max !== 1 ? 's' : ''} (has ${count})`
    });
  }

  (config.treatmentTypeLimits ?? []).forEach((limit) => {
    const typeCount = modules.filter((m) => m.ttKey === limit.ref).length;
    if (limit.min != null && typeCount < limit.min) {
      violations.push({
        message: `${limit.ref}: needs at least ${limit.min} module${limit.min !== 1 ? 's' : ''} (has ${typeCount})`
      });
    }
    if (limit.max != null && typeCount > limit.max) {
      violations.push({
        message: `${limit.ref}: exceeds maximum of ${limit.max} module${limit.max !== 1 ? 's' : ''} (has ${typeCount})`
      });
    }
  });

  return violations;
}

export interface LayoutValidationError {
  groupLabel: string;
  violations: GroupViolation[];
}

export function validateLayout(
  layout: EditorLayout,
  modulesByGroupId: Map<string, FlatSlot[]>
): LayoutValidationError[] {
  const errors: LayoutValidationError[] = [];
  (layout.structure?.groups ?? []).forEach((group) => {
    const modules = modulesByGroupId.get(group.id) ?? [];
    const violations = validateGroup(group.config, modules);
    if (violations.length > 0) {
      errors.push({ groupLabel: group.config.label, violations });
    }
  });
  return errors;
}

export function canRemoveModule(
  config: StructureGroupConfig,
  modules: FlatSlot[],
  moduleToRemove: FlatSlot
): boolean {
  if (config.min != null && modules.length <= config.min) return false;
  const tt = moduleToRemove.ttKey;
  const limit = config.treatmentTypeLimits?.find((l) => l.ref === tt);
  if (limit?.min != null) {
    const typeCount = modules.filter((m) => m.ttKey === tt).length;
    if (typeCount <= limit.min) return false;
  }
  return true;
}

/**
 * Extracts the layoutId from the first item in a page's root collection.
 * Returns undefined if not found.
 */
export const getLayoutIdFromPageRoot = (root?: unknown): string | undefined => {
  if (!root || typeof root !== 'object') {
    return undefined;
  }

  const rootObj = root as { collection?: unknown[] };
  const firstItem = rootObj.collection?.[0];

  if (firstItem && typeof firstItem === 'object' && 'attributes' in firstItem) {
    const { attributes } = firstItem as { attributes?: { layoutId?: string | null } };
    return attributes?.layoutId ?? undefined;
  }

  return undefined;
};

/**
 * Determines which layout allessehId to fetch for a new page.
 * Priority: URL param > baseline's layoutId > baseline
 *
 * @param layoutIdParam - Layout ID from URL parameter
 * @param baselinePageRoot - The root structure from baseline page
 * @param baselineAllessehId - The baseline page allessehId (usually NEW_PAGE_ID)
 * @param getCollectionIdPrefix - Function to get the collection ID prefix
 * @param currentProperty - The current property (e.g., 'wsj', 'barrons')
 * @returns The allessehId to fetch
 */
export const determineLayoutToFetch = ({
  layoutIdParam,
  baselinePageRoot,
  baselineAllessehId,
  getCollectionIdPrefix,
  currentProperty
}: {
  layoutIdParam: string | null;
  baselinePageRoot?: unknown;
  baselineAllessehId: string;
  getCollectionIdPrefix: () => string;
  currentProperty: string;
}): string => {
  // URL parameter takes priority
  if (layoutIdParam) {
    return `${getCollectionIdPrefix()}${currentProperty.toUpperCase()}_${layoutIdParam.toUpperCase()}`;
  }

  // Check if baseline has a custom layoutId
  const baselineLayoutId = getLayoutIdFromPageRoot(baselinePageRoot);
  if (baselineLayoutId && baselineLayoutId !== 'baseline') {
    return `${getCollectionIdPrefix()}${currentProperty.toUpperCase()}_${baselineLayoutId.toUpperCase()}`;
  }

  // Default to baseline
  return baselineAllessehId;
};
